import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { sortBy, isShallowEqual, nonNullable } from 'lib/array/arrayUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import type { TripItineraryData } from 'tripPlanner/api/types'
import {
  getCartPanelExperienceCartTime,
  getMultiCartExperienceCheckoutItem,
  isCartPanelExperienceBookingUnavailableStatus,
  isCartPanelItemCheckoutDataLoading,
  isCartPanelLinkedCheckoutProjectionPending,
  isMultiCartItemCheckoutActive,
  isMultiCartItemPurchased,
  resolveCartPanelExperienceCheckoutPrompt,
  resolveCartPanelExperienceDisplayStatus,
  resolveCartPanelCartQueryStatus,
  resolveCartPanelHotelCheckoutPrompt,
  toCartPanelExperienceTickets,
  type CartPanelCartQueryStatus,
} from '../utils/cartPanelUtils'
import {
  collectSavedFlightSearchExpiries,
  extractFlightCartPanelOptions,
  toFlightCartPanelProjectionSignature,
  type ExtractedFlightCartPanelOption,
} from '../utils/flightCartPanelUtils'
import {
  extractTripChatCartCandidates,
  toTripChatCartCandidateSignature,
  type TripChatAccommodationCartCandidate,
  type TripChatExperienceCartCandidate,
} from '../utils/tripChatCartCandidateUtils'
import {
  toTripChatAccommodationPricingInput,
  toTripChatExperiencePricingInput,
} from '../utils/tripChatCartPricingInputUtils'
import { getTripPlannerCandidateCartItem } from '../utils/tripPlannerCartItemsUtils'
import {
  toTripChatAccommodationCartSelection,
  toTripChatCartSelection,
  toTripChatExperienceCartSelection,
} from '../utils/tripChatCartSelectionUtils'
import type { ResolvedItemPricing, PricingInput } from './useItineraryItemPricing'
import { useItineraryItemPricing } from './useItineraryItemPricing'
import { useExperienceCartItemPricing, type ExperiencePricingInput } from './useExperienceCartItemPricing'
import { useFlightCartExpiryTick } from './useFlightCartExpiryTick'
import { groupMultiCityFlightItems } from './multiCityFlightGrouping'
import {
  tripPlannerCartItemsKey,
  useTripPlannerCartItems,
} from './useTripPlannerCartItems'
import type {
  CartPanelAccommodationItem,
  CartPanelExperienceItem,
  CartPanelFlightItem,
  CartPanelItem,
  CartPanelItemPricing,
} from './cartPanelItemTypes'

export type {
  CartPanelAccommodationItem,
  CartPanelExperienceItem,
  CartPanelFlightItem,
  CartPanelItem,
  CartPanelItemPricing,
} from './cartPanelItemTypes'

interface UseCartPanelItemsArgs {
  multiCartId: string | undefined
  itineraryData: TripItineraryData | undefined
  isLoggedIn: boolean
  optimisticExclusions: Record<number, boolean>
  refreshFailedFlightKeys: Record<string, boolean>
}

interface UseCartPanelItemsReturn {
  items: Array<CartPanelItem>
  isLoading: boolean
  cartQueryStatus: CartPanelCartQueryStatus
  isRetryingCartLoad: boolean
  retryCartLoad: () => void
  refetchCartItems: () => Promise<Map<string, App.MultiCartItem>>
}

type ExtractedAccommodationItem = TripChatAccommodationCartCandidate
type ExtractedExperienceItem = TripChatExperienceCartCandidate

interface CartPanelFlightRefreshKeyInput {
  tripItemId: number
  cartItemId: string | undefined
}

export function getCartPanelFlightRefreshKey(item: CartPanelFlightRefreshKeyInput): string {
  return item.cartItemId ?? `${item.tripItemId}`
}

function getCartItemSource(cartItem: App.MultiCartItem | undefined): 'cart' | 'itinerary' {
  return cartItem ? 'cart' : 'itinerary'
}

function hasLoadingIncludedPrice(item: CartPanelItem): boolean {
  return item.includeInTotal && isCartPanelItemCheckoutDataLoading(item)
}

function mapAccommodationItem(
  item: ExtractedAccommodationItem,
  pricing: ResolvedItemPricing,
  cartItem: App.MultiCartItem | undefined,
  cartExcluded: boolean,
  cartQueryStatus: CartPanelCartQueryStatus,
): CartPanelAccommodationItem {
  const includeInTotal = (
    item.cartStatus === 'active' &&
    pricing.isAvailable !== false &&
    isMultiCartItemCheckoutActive(cartItem)
  )

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(cartItem),
    cartStatus: item.cartStatus,
    cartExcluded,
    productType: 'accommodation',
    isBooked: item.isBooked,
    name: item.name,
    imageId: item.imageId,
    roomImageId: item.roomImageId,
    checkIn: item.checkIn,
    checkOut: item.checkOut,
    dates: item.checkIn && item.checkOut
      ? formatDynamicDateRange(item.checkIn, item.checkOut)
      : undefined,
    duration: item.duration,
    packageId: item.packageId,
    roomRateId: item.roomRateId,
    roomName: item.roomName,
    occupancy: item.occupancy,
    packageAutoSelected: item.packageAutoSelected,
    availableDurations: item.availableDurations,
    pricing,
    includeInTotal,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    offerId: item.offerId,
    occupancies: item.occupancies,
    charterFlightBundle: item.charterFlightBundle,
    cartItem,
    checkoutPrompt: resolveCartPanelHotelCheckoutPrompt({
      cartStatus: item.cartStatus,
      cartExcluded,
      checkIn: item.checkIn,
      checkOut: item.checkOut,
      cartItem,
      isAvailable: pricing.isAvailable,
    }, cartQueryStatus),
    cartSelection: toTripChatAccommodationCartSelection(item),
  }
}

function mapExperienceItem(
  item: ExtractedExperienceItem,
  pricing: CartPanelItemPricing,
  cartItem: App.MultiCartItem | undefined,
  cartExcluded: boolean,
  cartQueryStatus: CartPanelCartQueryStatus,
): CartPanelExperienceItem {
  const experienceCartItem = cartItem?.type === 'experience' ? cartItem : undefined
  const experienceCheckoutItem = getMultiCartExperienceCheckoutItem(experienceCartItem)
  const tickets = experienceCheckoutItem ? toCartPanelExperienceTickets(experienceCheckoutItem.tickets) : item.tickets
  const cartStatus = resolveCartPanelExperienceDisplayStatus({
    cartStatus: item.cartStatus,
    bookingType: item.bookingType,
    isBuyNowBookLater: item.isBuyNowBookLater,
    isGift: experienceCartItem?.isGift ?? false,
    cartItem: experienceCartItem,
    hasSelectedTickets: tickets.length > 0,
    isAvailable: pricing.isAvailable,
  })
  const date = experienceCartItem?.date ?? item.date
  const time = experienceCartItem ? getCartPanelExperienceCartTime(experienceCartItem) : item.time
  const isBuyNowBookLater = experienceCartItem?.isBuyNowBookLater ?? item.isBuyNowBookLater
  const includeInTotal = !isCartPanelExperienceBookingUnavailableStatus(cartStatus)

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(cartItem),
    cartStatus,
    cartExcluded,
    productType: 'experience',
    isBooked: item.isBooked,
    name: item.name,
    imageId: item.imageId,
    pricing,
    includeInTotal,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    cartItem,
    experienceId: item.experienceId,
    date,
    itineraryDate: item.itineraryDate,
    time,
    tickets,
    locationName: item.locationName,
    bookingType: item.bookingType,
    isBuyNowBookLater,
    checkoutPrompt: resolveCartPanelExperienceCheckoutPrompt({
      cartStatus,
      cartExcluded,
      cartItem,
      isAvailable: pricing.isAvailable,
    }, cartQueryStatus),
    cartSelection: toTripChatExperienceCartSelection(item),
  }
}

function getOptimisticFlightExclusion(
  item: ExtractedFlightCartPanelOption,
  optimisticExclusions: Record<number, boolean>,
): boolean {
  for (const tripItemId of item.pairedTripItemIds) {
    const optimisticValue = optimisticExclusions[tripItemId]
    if (optimisticValue !== undefined) return optimisticValue
  }

  return item.cartExcluded
}

function mapFlightItem(
  item: ExtractedFlightCartPanelOption,
  cartExcluded: boolean,
  hasRefreshFailed: boolean,
): CartPanelFlightItem {
  const memberPrice = item.cartStatus === 'active' && item.cartPrice !== undefined
    ? item.cartPrice
    : item.savedPrice
  const regularPrice = item.cartStatus === 'active' && item.cartRegularPrice !== undefined
    ? item.cartRegularPrice
    : item.savedRegularPrice
  const price = regularPrice ?? memberPrice
  const displayMemberPrice = regularPrice !== undefined ? memberPrice : undefined

  return {
    key: `${item.tripItemId}`,
    source: getCartItemSource(item.cartItem),
    cartStatus: item.cartStatus,
    cartExcluded,
    productType: 'flight',
    isBooked: false,
    name: item.option.label ?? 'Flight',
    imageId: item.savedItemData.carrierLogo,
    pricing: {
      price,
      memberPrice: displayMemberPrice,
      value: undefined,
      isAvailable: !item.isAvailabilityBlocked,
      loading: false,
      error: false,
    },
    includeInTotal: item.includeInTotal,
    cartItemId: item.cartItemId,
    tripItemId: item.tripItemId,
    cartItem: item.cartItem,
    pairedTripItemIds: item.pairedTripItemIds,
    returnTripItemId: item.returnTripItemId,
    option: item.option,
    returnOption: item.returnOption,
    savedItemData: item.savedItemData,
    returnSavedItemData: item.returnSavedItemData,
    flightCartItem: item.cartItem,
    isAvailabilityBlocked: item.isAvailabilityBlocked,
    hasRefreshFailed,
  }
}

export function useCartPanelItems({
  multiCartId,
  itineraryData,
  isLoggedIn,
  optimisticExclusions,
  refreshFailedFlightKeys,
}: UseCartPanelItemsArgs): UseCartPanelItemsReturn {
  const queryClient = useQueryClient()

  const itineraryItems = useMemo(
    () => extractTripChatCartCandidates(itineraryData),
    [itineraryData],
  )

  const accommodationItems = useMemo(
    () => itineraryItems.filter((item): item is ExtractedAccommodationItem => item.productType === 'accommodation'),
    [itineraryItems],
  )

  const experienceItems = useMemo(
    () => itineraryItems.filter((item): item is ExtractedExperienceItem => item.productType === 'experience'),
    [itineraryItems],
  )

  const {
    data: cartData,
    cartItemsMap,
    isLoading: cartLoading,
    isError: cartError,
    isMissingCart,
    refetchCartItems,
  } = useTripPlannerCartItems({ multiCartId, isLoggedIn })
  const [isRetryingCartLoad, setIsRetryingCartLoad] = useState(false)
  const retryCartLoad = useCallback(() => {
    setIsRetryingCartLoad(true)
    refetchCartItems()
      .catch(() => undefined)
      .finally(() => setIsRetryingCartLoad(false))
  }, [refetchCartItems])
  const cartQueryStatus = resolveCartPanelCartQueryStatus(isLoggedIn, {
    isLoading: cartLoading,
    isError: cartError,
    isMissingCart,
    hasData: cartData !== undefined,
    isRetryingFailedLoad: isRetryingCartLoad,
  })
  const savedFlightSearchExpiries = useMemo(() => {
    if (!itineraryData?.stops) return []
    return itineraryData.stops.flatMap(stop => collectSavedFlightSearchExpiries(stop.transports))
  }, [itineraryData])
  const flightCartNow = useFlightCartExpiryTick(cartItemsMap, savedFlightSearchExpiries)

  const pricingInputs = useMemo<Array<PricingInput>>(
    () => accommodationItems.map(item => toTripChatAccommodationPricingInput(item, getTripPlannerCandidateCartItem(cartItemsMap, toTripChatAccommodationCartSelection(item)))),
    [accommodationItems, cartItemsMap],
  )

  const experiencePricingInputs = useMemo<Array<ExperiencePricingInput>>(
    () => experienceItems.map(item => toTripChatExperiencePricingInput(item, getTripPlannerCandidateCartItem(cartItemsMap, toTripChatExperienceCartSelection(item)))),
    [experienceItems, cartItemsMap],
  )

  const pricing = useItineraryItemPricing(pricingInputs)
  const experiencePricing = useExperienceCartItemPricing(experiencePricingInputs, isLoggedIn)

  const flightItems = useMemo<Array<ExtractedFlightCartPanelOption>>(() => {
    if (!itineraryData?.stops) return []
    return itineraryData.stops.flatMap(stop => extractFlightCartPanelOptions(stop.transports, cartItemsMap, cartQueryStatus === 'loaded', flightCartNow))
  }, [itineraryData, cartItemsMap, cartQueryStatus, flightCartNow])

  const syncedCartItemSignatures = useMemo(
    () => nonNullable([
      ...itineraryItems.map(toTripChatCartCandidateSignature),
      ...flightItems.map(toFlightCartPanelProjectionSignature),
    ]),
    [itineraryItems, flightItems],
  )

  const prevCartItemSignaturesRef = useRef<Array<string>>([])
  useEffect(() => {
    const currentSignatures = sortBy(syncedCartItemSignatures, signature => signature, 'asc')
    if (prevCartItemSignaturesRef.current.length > 0 && !isShallowEqual(prevCartItemSignaturesRef.current, currentSignatures)) {
      if (isLoggedIn && multiCartId) {
        queryClient.invalidateQueries({ queryKey: tripPlannerCartItemsKey(multiCartId) }).catch(() => undefined)
      }
    }
    prevCartItemSignaturesRef.current = currentSignatures
  }, [syncedCartItemSignatures, multiCartId, isLoggedIn, queryClient])

  const items = useMemo<Array<CartPanelItem>>(() => {
    const accommodationAndExperienceItems = nonNullable(
      itineraryItems.map((item): CartPanelItem | undefined => {
        const cartItem = getTripPlannerCandidateCartItem(cartItemsMap, toTripChatCartSelection(item))
        const effectiveExcluded = optimisticExclusions[item.tripItemId] ?? item.cartExcluded

        if (item.productType === 'accommodation') {
          const resolvedPricing = pricing.get(item.tripItemId)
          if (!resolvedPricing) return undefined
          return mapAccommodationItem(item, resolvedPricing, cartItem, effectiveExcluded, cartQueryStatus)
        }

        const resolvedPricing = experiencePricing.get(item.tripItemId)
        if (!resolvedPricing) return undefined
        return mapExperienceItem(item, resolvedPricing, cartItem, effectiveExcluded, cartQueryStatus)
      }),
    )

    const mappedFlightItems = flightItems.map(item => {
      const refreshKey = getCartPanelFlightRefreshKey(item)
      return mapFlightItem(
        item,
        getOptimisticFlightExclusion(item, optimisticExclusions),
        refreshFailedFlightKeys[refreshKey] ?? false,
      )
    })

    // Collapse multi-city legs (shared flightGroupId) into a single grouped
    // entry so the itinerary is treated as one flight everywhere downstream.
    const groupedFlightItems = groupMultiCityFlightItems(mappedFlightItems)

    // A purchased row that no longer matches the itinerary still leaves the cart.
    return [...accommodationAndExperienceItems, ...groupedFlightItems].filter(item => (
      !isMultiCartItemPurchased(item.cartItem ?? (item.cartItemId ? cartItemsMap.get(item.cartItemId) : undefined))
    ))
  }, [itineraryItems, pricing, experiencePricing, cartItemsMap, optimisticExclusions, flightItems, refreshFailedFlightKeys, cartQueryStatus])

  const isWaitingForCheckoutProjection = cartLoading && (
    items.length === 0 ||
    items.some(isCartPanelLinkedCheckoutProjectionPending)
  )
  const isLoading = isWaitingForCheckoutProjection || items.some(hasLoadingIncludedPrice)

  return {
    items,
    isLoading,
    cartQueryStatus,
    isRetryingCartLoad,
    retryCartLoad,
    refetchCartItems,
  }
}
